/**
 * Algolia search client factory
 * Creates and optionally cache-wraps an Algolia search client.
 * Consolidates client creation logic from 3 verification JS files.
 */

import { wrapWithLocalStorageCache, type AlgoliaCacheOptions } from './algolia-cache';
import { parseTestParams } from './algolia-test-params';

export interface AlgoliaConfig {
  appId: string;
  apiKey: string;
  indexName: string;
  connected: boolean;
  customer: {
    loggedIn: boolean;
    ordersCount: number;
    tags: string[];
    b2b: boolean;
    companyId: number | string | null;
    wishlistVariantIds?: Array<number | string>;
  } | null;
}

interface SearchClient {
  search: (requests: unknown[]) => Promise<unknown>;
  [key: string]: unknown;
}

declare global {
  interface Window {
    __ALGOLIA_CONFIG__?: AlgoliaConfig;
    algoliasearch?: (appId: string, apiKey: string) => SearchClient;
    ['@algolia/autocomplete-js']?: unknown;
  }
}

/**
 * Get the Algolia config from the global window object.
 * Set by algolia-config.liquid.
 */
export function getAlgoliaConfig(): AlgoliaConfig | null {
  return window.__ALGOLIA_CONFIG__ ?? null;
}

/**
 * Create a search client, optionally wrapping with localStorage cache.
 *
 * @param cacheOptions - Cache options for localStorage wrapper. If cacheEnabled is false in URL params, caching is skipped.
 * @returns Search client or null if algoliasearch is not available
 */
export function createSearchClient(
  cacheOptions?: AlgoliaCacheOptions,
): SearchClient | null {
  const config = getAlgoliaConfig();
  if (!config || !config.connected || !window.algoliasearch) {
    return null;
  }

  const rawClient = window.algoliasearch(config.appId, config.apiKey);
  const testParams = parseTestParams();

  if (testParams.cacheEnabled && cacheOptions) {
    return wrapWithLocalStorageCache(rawClient, cacheOptions);
  }

  return rawClient;
}

/**
 * Wait for Algolia CDN libraries to load.
 * Polls for window.algoliasearch (and optionally autocomplete).
 *
 * @param options - Which libraries to wait for
 * @returns Promise that resolves when libraries are available, or rejects after timeout
 */
export function waitForAlgoliaLibs(
  options: { autocomplete?: boolean } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const POLL_INTERVAL = 100;
    const TIMEOUT = 10000;

    const check = (): boolean => {
      if (typeof window.algoliasearch === 'undefined') return false;
      if (
        options.autocomplete &&
        typeof window['@algolia/autocomplete-js'] === 'undefined'
      )
        return false;
      return true;
    };

    if (check()) {
      resolve();
      return;
    }

    const interval = setInterval(() => {
      if (check()) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      }
    }, POLL_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!check()) {
        reject(new Error('Algolia libraries failed to load within timeout'));
      }
    }, TIMEOUT);
  });
}
